import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useApi, useLive, useNow } from '../hooks';
import { post } from '../api';
import { CHANNEL_NAME, intentLabel, timeAgo, who } from '../format';
import { Badge, Button, Empty, ErrorNote, Loading, PageHead, Tabs, WindowMeter, useAction } from '../components/ui';
import { canWrite, useAuth } from '../auth';

export default function Approvals() {
  const [status, setStatus] = useState<'pending' | 'all'>('pending');
  const list = useApi<any[]>(`/approvals?status=${status}`, [status]);
  useLive((e) => { if (e.type.startsWith('approval')) list.reload(); });
  const now = useNow(30000);
  return (
    <div className="page">
      <PageHead title="Approvals" sub="Drafts the assistant held back: complaints, refunds, and anything the compliance check flagged. Soonest Meta deadline first." />
      <Tabs tabs={[{ id: 'pending', label: `Waiting${list.data && status === 'pending' ? ` (${list.data.length})` : ''}` }, { id: 'all', label: 'History' }]} value={status} onChange={setStatus} />
      {list.loading && !list.data ? <Loading /> : list.error ? <ErrorNote message={list.error} onRetry={list.reload} />
        : !list.data?.length ? <Empty title={status === 'pending' ? 'All caught up' : 'No approvals yet'}>{status === 'pending' ? 'Nothing is waiting. New drafts appear here the moment the assistant holds one.' : 'Approved, edited, and rejected drafts will show here.'}</Empty>
        : <div className="stack">{list.data.map((a) => <ApprovalCard key={a.id} a={a} now={now} onDone={list.reload} />)}</div>}
    </div>
  );
}

function ApprovalCard({ a, now, onDone }: { a: any; now: number; onDone: () => void }) {
  const { user } = useAuth();
  const [text, setText] = useState(a.final_text ?? a.draft);
  const { busy, run } = useAction();
  const pending = a.status === 'pending';
  const flagged = String(a.reason).startsWith('Guardrail') || String(a.reason).includes('flagged');
  const customer = a.comment_body ? `@${a.comment_author ?? 'someone'} commented` : who(a);
  return (
    <article className="card approval">
      <div className="stack">
        <div className="row">
          <strong>{customer}</strong>
          <span className="chan">{a.comment_platform ? (a.comment_platform === 'instagram' ? 'IG comment' : 'FB comment') : CHANNEL_NAME[a.channel] ?? ''}</span>
          {a.intent && <Badge tone="neutral">{intentLabel(a.intent)}</Badge>}
          {flagged ? <Badge tone="danger">Compliance flag</Badge> : <Badge tone="warning">Needs approval</Badge>}
          {!pending && <Badge tone={a.status === 'rejected' || a.status === 'expired' ? 'neutral' : 'success'}>{a.status}</Badge>}
          <span className="spacer" /><span className="xs muted">{timeAgo(a.created_at, now)} ago</span>
        </div>
        <div className="quote">{a.comment_body ?? a.customer_message ?? 'No customer message captured'}</div>
        <div className={`note ${flagged ? 'error' : 'warn'}`}>{a.reason}</div>
        <textarea className="textarea" value={text} disabled={!pending || !canWrite(user)} onChange={(e) => setText(e.target.value)} aria-label="Reply to send" rows={4} />
        {pending && canWrite(user) && (
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            {a.conversation_id && <Link className="btn ghost sm" to={`/inbox/${a.conversation_id}`}>Open conversation</Link>}
            <Button variant="danger" size="sm" loading={busy === 'r'} onClick={async () => { await run('r', () => post(`/approvals/${a.id}/reject`), 'Draft rejected'); onDone(); }}>Reject</Button>
            <Button variant="primary" loading={busy === 'a'} onClick={async () => { await run('a', () => post(`/approvals/${a.id}/approve`, { text }), text.trim() !== a.draft.trim() ? 'Edited reply sent' : 'Approved and sent'); onDone(); }}>{text.trim() !== a.draft.trim() ? 'Send edited reply' : 'Approve and send'}</Button>
          </div>
        )}
      </div>
      <div className="stack" style={{ alignContent: 'start' }}>
        {a.window_expires_at ? <WindowMeter expiresAt={a.window_expires_at} now={now} /> : <span className="xs muted">No reply deadline on this channel</span>}
        {a.trace?.kb?.length > 0 && <div className="xs"><strong>Sources</strong><div className="muted">{a.trace.kb.map((k: any) => k.title).join(', ')}</div></div>}
        {a.trace?.guardrail?.issues?.length > 0 && <div className="xs"><strong>Checks that failed</strong><ul style={{ margin: '4px 0', paddingLeft: 16 }}>{a.trace.guardrail.issues.map((i: any, n: number) => <li key={n}>{i.detail}</li>)}</ul></div>}
      </div>
    </article>
  );
}
